import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['item', 'secondaryItem', 'focus', 'chevron', 'ignore'];

  static values = {
    shortcut: String,
    skipAnimation: Boolean,
    chevronRotate: { type: Number, default: 180 },
    closeOnClickOutside: { type: Boolean, default: true },
  };

  // Needed to make sure that Tailwind JIT compiles the classes
  rotations = {
    180: 'rotate-180',
    90: 'rotate-90',
  };

  connect() {
    this.element.setAttribute('data-reveal-controller-connected', 'true');
    this._onKeyDown = this.onKeyDownWindow.bind(this);
    window.addEventListener('keydown', this._onKeyDown);

    this.addFadeInAnimation();

    this.onClickWindow = this.onClickWindow.bind(this);

    if (this.closeOnClickOutsideValue) {
      window.addEventListener('click', this.onClickWindow, true);
    }
  }

  chevronTargetConnected(target) {
    target.classList.add('transition-transform');
  }

  disconnect() {
    window.removeEventListener('keydown', this._onKeyDown);
    if (this.closeOnClickOutsideValue) {
      window.removeEventListener('click', this.onClickWindow, true);
    }
  }

  onClickWindow(e) {
    const clickedOutside = !this.itemTargets.some((target) => target.contains(e.target));
    const clickedOutsideSecondary = !this.secondaryItemTargets.some((target) =>
      target.contains(e.target),
    );
    const clickedOnIngored = this.ignoreTargets.some((target) => target.contains(e.target));
    const clickedTurboConfirm = document.getElementById('turbo-confirm-modal')?.contains(e.target);

    // Only close main dropdown when click is outside both main and secondary panels.
    // Otherwise a click in the secondary (e.g. tag label) would close the entire dropdown.
    if (
      clickedOutside &&
      (clickedOutsideSecondary || !this.hasSecondaryItemTarget) &&
      !clickedOnIngored &&
      !clickedTurboConfirm &&
      this.elementVisible()
    ) {
      this.close(e);
      // Only stop propagation if the click isn't inside another visible reveal panel,
      // otherwise we'd swallow clicks meant for other reveal-controlled content (e.g. search results).
      if (!e.target.closest('[data-reveal-target="item"]:not(.hidden)')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    if (
      clickedOutsideSecondary &&
      !clickedOnIngored &&
      !clickedTurboConfirm &&
      this.secondaryElementVisible()
    ) {
      this.closeSecondary(e);
      if (!e.target.closest('[data-reveal-target="item"]:not(.hidden)')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }

  onKeyDownWindow(e) {
    if ('key' in e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (this.hasItemTarget && this.elementVisible()) {
          this.close();
        }
        if (this.hasSecondaryItemTarget && this.secondaryElementVisible()) {
          this.closeSecondary();
        }
        // For now it assumes that shortcut key is combined with meta key
      } else if (e.key === this.shortcutValue && e.metaKey) {
        this.show();
        this.showSecondary();
      }
    }
  }

  toggle() {
    for (const target of this.itemTargets) {
      target.classList.toggle('hidden');
      this.dispatch('toggle', { detail: { item: target } });
    }

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle(this.rotations[this.chevronRotateValue]);
    }
  }

  toggleSecondary() {
    for (const target of this.secondaryItemTargets) {
      target.classList.toggle('hidden');
      this.dispatch('toggle', { detail: { item: target } });
    }
  }

  toggleSecondaryStop(event) {
    event.stopPropagation();
    this.toggleSecondary();
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  show() {
    for (const target of this.itemTargets) {
      if (this.hasFocusTarget) {
        if (this.skipAnimationValue) {
          // No animation classes get added, so animationend never fires:
          // focus on the next frame instead.
          requestAnimationFrame(() => this.focusTarget.focus());
        } else {
          const focusAfterAnimation = () => {
            this.focusTarget.focus();
            target.removeEventListener('animationend', focusAfterAnimation);
          };

          target.addEventListener('animationend', focusAfterAnimation);
        }
      }

      target.classList.forEach((cls) => {
        if (cls === 'hidden' || cls.endsWith(':hidden')) {
          target.classList.remove(cls);
          this.dispatch('show', { detail: { item: target } });
        }
      });
    }

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.add(this.rotations[this.chevronRotateValue]);
    }
  }

  showSecondary() {
    for (const target of this.secondaryItemTargets) {
      target.classList.forEach((cls) => {
        if (cls === 'hidden' || cls.endsWith(':hidden')) {
          target.classList.remove(cls);
          this.dispatch('show', { detail: { item: target } });
        }
      });
    }
  }

  hide() {
    this.close();
  }

  hideSecondary() {
    this.closeSecondary();
  }

  close = (event) => {
    for (const item of this.itemTargets) {
      item.classList.add('hidden');
      this.dispatch('hide', { detail: { item, target: event?.target } });
    }

    if (this.hasChevronTarget) {
      this.chevronTarget.classList.remove(this.rotations[this.chevronRotateValue]);
    }
  };

  closeSecondary = (event) => {
    for (const item of this.secondaryItemTargets) {
      item.classList.add('hidden');
      this.dispatch('hide', { detail: { item, target: event?.target } });
    }
  };

  elementVisible() {
    // check that classlist has hidden don't confuse with overflow-hidden
    return this.hasItemTarget && !this.itemTarget.classList.contains('hidden');
  }

  secondaryElementVisible() {
    // check that classlist has hidden don't confuse with overflow-hidden
    return this.hasSecondaryItemTarget && !this.secondaryItemTarget.classList.contains('hidden');
  }

  addFadeInAnimation() {
    // Automatically add fade-in animation, except from modals which are already animated
    for (const target of this.itemTargets) {
      const animatedChildren = Array.from(target.children).some((child) =>
        child.classList.contains('animate-fade-in'),
      );
      if (!animatedChildren && !this.skipAnimationValue) {
        target.classList.add('animate-fade-in');
      }
    }

    for (const target of this.secondaryItemTargets) {
      const animatedChildren = Array.from(target.children).some((child) =>
        child.classList.contains('animate-fade-in'),
      );
      if (!animatedChildren && !this.skipAnimationValue) {
        target.classList.add('animate-fade-in');
      }
    }
  }
}
